import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "../context/AuthContext";
import api from "../config/api";
import toast from "react-hot-toast";

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const { user } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch wishlist items with caching
  const fetchWishlist = useCallback(
    async (forceRefresh = false) => {
      if (!user) {
        setWishlist([]);
        setWishlistCount(0);
        setIsInitialized(true);
        return;
      }

      // Cache for 5 minutes to prevent excessive API calls
      const now = Date.now();
      if (!forceRefresh && isInitialized && now - lastFetchTime < 300000) {
        return;
      }

      try {
        setLoading(true);
        const response = await api.get("/wishlist");
        setWishlist(response.data.data || []);
        setWishlistCount(response.data.count || 0);
        setLastFetchTime(now);
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to fetch wishlist:", error);
        setWishlist([]);
        setWishlistCount(0);
      } finally {
        setLoading(false);
      }
    },
    [user, isInitialized, lastFetchTime]
  );

  // Fetch wishlist count only
  const fetchWishlistCount = useCallback(async () => {
    if (!user) {
      setWishlistCount(0);
      return;
    }

    try {
      const response = await api.get("/wishlist/count");
      setWishlistCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to fetch wishlist count:", error);
      setWishlistCount(0);
    }
  }, [user]);

  // Check if product is in wishlist
  const isInWishlist = useCallback(
    (productId) => {
      return wishlist.some((item) => item.product_id === productId);
    },
    [wishlist]
  );

  // Toggle wishlist item with optimistic updates
  const toggleWishlist = useCallback(
    async (productId) => {
      if (!user) {
        toast.error("Please login to manage your wishlist");
        return false;
      }

      const isCurrentlyInWishlist = isInWishlist(productId);

      // Optimistic update
      if (isCurrentlyInWishlist) {
        setWishlistCount((prev) => Math.max(0, prev - 1));
        setWishlist((prev) =>
          prev.filter((item) => item.product_id !== productId)
        );
      } else {
        setWishlistCount((prev) => prev + 1);
      }

      try {
        const response = await api.post("/wishlist/toggle", {
          product_id: productId,
        });

        if (response.data.success) {
          // Update last fetch time to prevent immediate refetch
          setLastFetchTime(Date.now());

          if (response.data.action === "added") {
            toast.success("Added to wishlist");
          } else {
            toast("Removed from wishlist", {
              icon: "❌",
              style: { color: "#fff", background: "#ef4444" },
            });
          }
          return true;
        } else {
          // Revert optimistic update on failure
          if (isCurrentlyInWishlist) {
            setWishlistCount((prev) => prev + 1);
            await fetchWishlist(true); // Force refresh to get correct state
          } else {
            setWishlistCount((prev) => Math.max(0, prev - 1));
            await fetchWishlist(true); // Force refresh to get correct state
          }
          return false;
        }
      } catch (error) {
        console.error("Failed to toggle wishlist:", error);

        // Revert optimistic update on error
        if (isCurrentlyInWishlist) {
          setWishlistCount((prev) => prev + 1);
          await fetchWishlist(true); // Force refresh to get correct state
        } else {
          setWishlistCount((prev) => Math.max(0, prev - 1));
          await fetchWishlist(true); // Force refresh to get correct state
        }

        toast.error(
          error.response?.data?.message || "Failed to update wishlist"
        );
        return false;
      }
    },
    [user, isInWishlist, fetchWishlist]
  );

  // Add to wishlist
  const addToWishlist = useCallback(
    async (productId) => {
      if (!user) {
        toast.error("Please login to add items to wishlist");
        return false;
      }

      if (isInWishlist(productId)) {
        toast.info("Product is already in your wishlist");
        return false;
      }

      // Optimistic update
      setWishlistCount((prev) => prev + 1);

      try {
        const response = await api.post("/wishlist", {
          product_id: productId,
        });

        if (response.data.success) {
          setLastFetchTime(Date.now());
          toast.success("Added to wishlist");
          return true;
        } else {
          // Revert optimistic update
          setWishlistCount((prev) => Math.max(0, prev - 1));
          return false;
        }
      } catch (error) {
        console.error("Failed to add to wishlist:", error);
        // Revert optimistic update
        setWishlistCount((prev) => Math.max(0, prev - 1));
        toast.error(
          error.response?.data?.message || "Failed to add to wishlist"
        );
        return false;
      }
    },
    [user, isInWishlist]
  );

  // Remove from wishlist
  const removeFromWishlist = useCallback(
    async (productId) => {
      if (!user) return false;

      // Optimistic update
      setWishlistCount((prev) => Math.max(0, prev - 1));
      setWishlist((prev) =>
        prev.filter((item) => item.product_id !== productId)
      );

      try {
        const response = await api.delete(`/wishlist/product/${productId}`);

        if (response.data.success) {
          setLastFetchTime(Date.now());
          toast("Removed from wishlist", {
            icon: "❌",
            style: { color: "#fff", background: "#ef4444" },
          });
          return true;
        } else {
          // Revert optimistic update
          await fetchWishlist(true);
          return false;
        }
      } catch (error) {
        console.error("Failed to remove from wishlist:", error);
        // Revert optimistic update
        await fetchWishlist(true);
        toast.error(
          error.response?.data?.message || "Failed to remove from wishlist"
        );
        return false;
      }
    },
    [user, fetchWishlist]
  );

  // Clear entire wishlist
  const clearWishlist = useCallback(() => {
    setWishlist([]);
    setWishlistCount(0);
    setIsInitialized(false);
    setLastFetchTime(0);
  }, []);

  // Load wishlist when user changes
  useEffect(() => {
    if (user) {
      fetchWishlist();
    } else {
      clearWishlist();
    }
  }, [user, fetchWishlist, clearWishlist]);

  const value = {
    wishlist,
    wishlistCount,
    loading,
    isInWishlist,
    toggleWishlist,
    addToWishlist,
    removeFromWishlist,
    fetchWishlist,
    fetchWishlistCount,
    clearWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};
